import { ExternalLink, Focus, GitBranch, X } from 'lucide-react';
import { nodeById, stages } from '../data/graph';
import { tr } from '../i18n';
import { useDiagramStore } from '../state/store';

const List = ({ items }: { items: string[] }) => items.length ? <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul> : <span>—</span>;

export function DetailsPanel() {
  const state = useDiagramStore();
  const node = state.selectedNodeId ? nodeById.get(state.selectedNodeId) : undefined;
  if (!node) return null;
  const stage = stages.find((item) => item.id === node.stageId)!;
  const confidenceKey = node.confidence === 'publicly-referenced' ? 'publiclyReferenced' : node.confidence === 'optional-example' ? 'optionalExample' : 'recommended';
  const roleKey = node.digitalMarketingRole === 'co-own' ? 'coOwn' : node.digitalMarketingRole;
  const names = (ids: string[]) => ids.map((id) => nodeById.get(id)?.title[state.language] ?? id);
  return (
    <aside className="details-panel" aria-label={node.title[state.language]} data-testid="details-panel">
      <div className="details-handle" />
      <header className="details-header">
        <div><span className="eyebrow">{stage.shortTitle[state.language]} · {node.variant?.toUpperCase()}</span><h2>{node.title[state.language]}</h2></div>
        <button className="icon-button" onClick={() => state.selectNode(null)} aria-label={tr(state.language, 'close')}><X size={18} /></button>
      </header>
      <div className="details-badges">
        {node.funnelStages.map((funnel) => <span key={funnel} className={`funnel-detail-badge funnel-detail-badge--${funnel}`}>{funnel.toUpperCase()}</span>)}
        <span className={`confidence-badge confidence-badge--${node.confidence}`}>{tr(state.language, confidenceKey)}</span>
        <span className={`role-badge role-badge--${node.digitalMarketingRole}`}>{tr(state.language, 'dmRole')}: {tr(state.language, roleKey)}</span>
      </div>
      <div className="details-actions">
        <button onClick={() => state.setFocusPath(!state.focusPath)} className={state.focusPath ? 'active' : ''}><GitBranch size={14} />{tr(state.language, state.focusPath ? 'showAll' : 'showPath')}</button>
        <button onClick={() => window.dispatchEvent(new CustomEvent('pfd:focus-selected'))}><Focus size={14} />{tr(state.language, 'startHere')}</button>
      </div>
      <div className="details-scroll">
        <section><h3>{tr(state.language, 'overview')}</h3><p>{node.summary[state.language]}</p></section>
        <section><h3>{tr(state.language, 'purpose')}</h3><p>{node.purpose[state.language]}</p></section>
        <section><h3>{tr(state.language, 'relevance')}</h3><p>{node.sinteksRelevance[state.language]}</p></section>
        <div className="details-grid">
          <section><h3>{tr(state.language, 'incoming')}</h3><List items={node.incomingData} /></section>
          <section><h3>{tr(state.language, 'outgoing')}</h3><List items={node.outgoingData} /></section>
          <section><h3>{tr(state.language, 'upstream')}</h3><List items={names(node.upstreamNodeIds)} /></section>
          <section><h3>{tr(state.language, 'downstream')}</h3><List items={names(node.downstreamNodeIds)} /></section>
        </div>
        <section><h3>{tr(state.language, 'useCase')}</h3><p>{node.exampleUseCase[state.language]}</p></section>
        <div className="details-grid">
          <section><h3>{tr(state.language, 'kpis')}</h3><List items={node.kpis} /></section>
          <section><h3>{tr(state.language, 'owners')}</h3><List items={node.owners} /></section>
        </div>
        <section><h3>{tr(state.language, 'integration')}</h3><List items={node.integrationNotes.map((item) => item[state.language])} /></section>
        <section><h3>{tr(state.language, 'governance')}</h3><List items={node.governanceNotes.map((item) => item[state.language])} /></section>
        <section className="source-note"><h3>{tr(state.language, 'sourceNote')}</h3><p>{node.sourceNote[state.language]}</p>
          {node.sourceUrl && <a href={node.sourceUrl} target="_blank" rel="noreferrer">{tr(state.language, 'openSource')} <ExternalLink size={13} /></a>}
        </section>
      </div>
    </aside>
  );
}
